// ScheduledSendsView — "Correos programados": la cola de envíos de la agencia.
//
// Hasta ahora la cola era invisible. El servidor sabía qué correos estaban
// esperando y tenía desde siempre una ruta para cancelarlos, pero **ninguna
// pantalla la llamaba**: nadie podía ver qué iba a salir ni pararlo. Cuando CR
// INSURANCE LATINO reportó "doble y triple felicitación de cumpleaños"
// (2026-08-15), Wendy pidió exactamente esto — "identifica a quién se le ha
// enviado más de 1 vez" — y no había dónde mirarlo.
//
// Tres cosas, entonces:
//   1. VER lo que está encolado y lo que ya salió.
//   2. CANCELAR lo pendiente antes de que se mande.
//   3. SEÑALAR los repetidos: mismo cliente, mismo tipo de saludo, mismo día.
//
// Y las dos mitades, porque si no la tercera es mentira: un saludo enviado en
// el momento (el botón "Enviar por Deskte") NO pasa por la cola — se guarda en
// los correos del cliente. El informe de repetidos que solo mirase la cola se
// dejaría fuera precisamente los envíos manuales, que son los del primer
// ticket, cuando alguien reprocesaba lo que veía en rojo. Media respuesta se
// lee como la respuesta entera, así que es peor que ninguna. Desde la columna
// `source` (17 de agosto) esos envíos vienen marcados y se cuentan aquí junto
// con los programados; lo anterior a esa fecha no se puede reconocer y la
// pantalla lo dice en vez de aparentar ser el total.

import { useEffect, useMemo, useState } from 'react';
import { Clock, RefreshCw, X, Search, AlertTriangle, Ban, Mail, Zap, Eye, Send, Pencil } from 'lucide-react';
import { Modal } from './ui.jsx';
import { safeHtml } from '../sanitizeHtml.js';
import { listScheduledSends, listSentGreetings, cancelScheduledSend, getScheduledSend, updateScheduledSend } from '../api.js';
import { confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import DateField from './DateField.jsx';
import { dejarPendiente, PENDIENTE_EMAIL_MASIVO } from '../enlacesDeLaFicha.js';

// Día LOCAL (YYYY-MM-DD). Nunca toISOString(): en la tarde-noche de Florida el
// día UTC ya es el siguiente, y dos saludos de la misma noche se contarían como
// días distintos — que es justo el error que dejaba pasar los duplicados.
const localDayKey = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return '';
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return localDayKey(d); };

// Las campañas quedan fuera del control de repetidos: dos boletines al mismo
// cliente el mismo día son legítimos. Dos cumpleaños no.
const ONE_PER_DAY = new Set(['birthday_greeting', 'holiday_greeting']);

const SOURCES = (es) => [
  { id: 'all', label: es ? 'Todos' : 'All' },
  { id: 'birthday_greeting', label: es ? '🎂 Cumpleaños' : '🎂 Birthdays' },
  { id: 'holiday_greeting', label: es ? '🎉 Fiestas' : '🎉 Holidays' },
  { id: 'campaign', label: es ? '📣 Campañas' : '📣 Campaigns' },
  { id: 'manual', label: es ? '✉️ Manual' : '✉️ Manual' },
];

const STATUS_META = (es) => ({
  pending:   { label: es ? 'Programado' : 'Scheduled', color: '#ffb700' },
  sent:      { label: es ? 'Enviado' : 'Sent',         color: '#00ff88' },
  failed:    { label: es ? 'Falló' : 'Failed',         color: '#ff3d6b' },
  // NO ES UN FALLO, y por eso no es rojo: el número está en la lista de
  // No-Textear y el sistema hizo lo que se le pidió. Si no estuviera aquí la
  // fila saldría con su código en crudo ("skipped") y, peor, la lista de abajo
  // ni siquiera la pediría — desaparecería sin decir nada.
  skipped:   { label: es ? 'Omitido' : 'Skipped',      color: '#7aa3c0' },
  cancelled: { label: es ? 'Cancelado' : 'Cancelled',  color: '#7aa3c0' },
});

export function ScheduledSendsView({ clients = [], users = [], currentUser, lang = 'es', onOpenClient, canManage = false }) {
  const es = lang === 'es';
  const STATUS = STATUS_META(es);

  const [fromDate, setFromDate] = useState(addDays(-7));
  const [toDate, setToDate] = useState(addDays(30));
  const [source, setSource] = useState('all');
  const [query, setQuery] = useState('');
  const [onlyDupes, setOnlyDupes] = useState(false);
  const [rows, setRows] = useState([]);
  // TRUE cuando solo se pudo leer la cola: el informe de repetidos estaría
  // contando de menos y hay que decirlo, no callarlo.
  const [partial, setPartial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  // Seleccion multiple: cancelar uno por uno no es viable cuando una campana
  // deja decenas o cientos programados (sugerencia recibida 2026-08-20).
  const [selMode, setSelMode] = useState(false);
  const [selIds, setSelIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Ver lo que dice un envio de la cola (Lissette 2026-09-02: "no me deja ver
  // lo que escribi en ese correo ni el boton de reenviar").
  //
  // El cuerpo NO viaja en la lista, y es a proposito: decision suya del
  // 2026-07-16 — en la vista de agencia se ve QUE hay algo encolado, no lo que
  // dice, porque puede ser de un companero. Asi que se pide UNO cuando se
  // pulsa, y el servidor decide si te toca (el autor, o un dueno).
  const [visor, setVisor] = useState(null); // {cargando, fila, error}
  // EDITAR lo que todavia no ha salido (Lissette, punto 75: *"que todo el
  // equipo de la misma agencia pueda editarlo o desprogramarla"*). Hasta ahora
  // la unica salida era cancelar y volver a programar desde cero.
  const [editor, setEditor] = useState(null); // {cargando, fila, error, fecha, hora, subject, body, guardando}
  const abrir = async (r) => {
    setVisor({ cargando: true, fila: null, error: '' });
    try {
      setVisor({ cargando: false, fila: await getScheduledSend(r.id), error: '' });
    } catch (e) {
      setVisor({
        cargando: false, fila: null,
        error: e?.code === 'NOT_YOURS'
          ? (es ? 'Esto lo programó otra persona, así que su contenido no se enseña aquí.' : 'Someone else scheduled this, so its content is not shown here.')
          : (e?.message || (es ? 'No se pudo abrir.' : 'Could not open.')),
      });
    }
  };

  // La fecha y la hora que se enseñan y se editan son las LOCALES del navegador
  // — nunca toISOString(), que en la tarde de Florida ya cuenta el dia
  // siguiente y moveria el envio un dia entero sin que nadie lo pidiera.
  const horaLocal = (d) => {
    const x = new Date(d);
    if (isNaN(x.getTime())) return '';
    return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
  };

  const abrirEditor = async (r) => {
    setEditor({
      cargando: true, fila: r, error: '', guardando: false,
      fecha: localDayKey(r.runAt), hora: horaLocal(r.runAt), subject: '', body: '',
    });
    try {
      // Se pide la fila completa para poder editar el texto de un envio suelto.
      // Si el servidor no la deja ver (sin permiso), la fecha se puede cambiar
      // igual: es lo que casi siempre se quiere mover.
      const full = await getScheduledSend(r.id);
      setEditor((e) => (e && e.fila.id === r.id
        ? { ...e, cargando: false, fila: { ...r, ...full }, subject: full?.subject || '', body: full?.body || '' }
        : e));
    } catch (err) {
      setEditor((e) => (e && e.fila.id === r.id
        ? { ...e, cargando: false, error: err?.message || (es ? 'No se pudo abrir el texto.' : 'Could not load the text.') }
        : e));
    }
  };

  const guardarEditor = async () => {
    const e = editor;
    if (!e) return;
    const [Y, M, D] = String(e.fecha || '').split('-').map(Number);
    const [h, m] = String(e.hora || '').split(':').map(Number);
    if (!Y || !M || !D || Number.isNaN(h) || Number.isNaN(m)) {
      toast.error(es ? 'Pon una fecha y una hora válidas.' : 'Enter a valid date and time.');
      return;
    }
    const cuando = new Date(Y, M - 1, D, h, m, 0, 0);
    const patch = { runAt: cuando.toISOString() };
    // El texto solo viaja si es un envio suelto Y de verdad cambio: mandarlo
    // sin tocarlo en una fila de campaña se llevaria un 400 por nada.
    if (!e.fila.campaignId && !e.cargando && !e.error) {
      if (e.subject !== (e.fila.subject || '')) patch.subject = e.subject;
      if (e.body !== (e.fila.body || '')) patch.body = e.body;
    }
    setEditor((x) => ({ ...x, guardando: true }));
    try {
      const res = await updateScheduledSend(e.fila.id, patch);
      const movidos = res?.movidos ?? 1;
      setRows((prev) => prev.map((x) => {
        const alcanzado = e.fila.campaignId
          ? (x.campaignId === e.fila.campaignId && x.status === 'pending')
          : x.id === e.fila.id;
        return alcanzado ? { ...x, runAt: cuando.toISOString() } : x;
      }).sort((a, b) => new Date(a.runAt) - new Date(b.runAt)));
      setEditor(null);
      toast.success(movidos > 1
        ? (es ? `Cambiado — se movieron ${movidos} correos de la campaña.` : `Saved — ${movidos} campaign emails moved.`)
        : (es ? 'Cambiado.' : 'Saved.'));
    } catch (err) {
      setEditor((x) => (x ? { ...x, guardando: false } : x));
      toast.error(err?.code === 'CAMPAIGN_BODY_IS_PERSONALIZED'
        ? (es
          ? 'El texto de una campaña no se cambia envío por envío: cada copia lleva ya el nombre de su destinatario. Desprográmala, edítala en Email Masivo y vuelve a programarla.'
          : "A campaign's text can't be changed one send at a time: each copy already carries its recipient's name. Unschedule it, edit it in Mass Email and schedule it again.")
        : (es ? 'No se pudo guardar: ' : "Couldn't save: ") + (err?.message || ''));
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    const filtro = source !== 'all' ? { source } : {};
    Promise.all([
      listScheduledSends({ scope: 'agency', status: 'pending,sent,failed,skipped', ...filtro, fromDate, toDate }),
      // Los enviados en el momento. Si esta mitad falla, la otra se muestra
      // igual: una cola incompleta es más útil que una pantalla en blanco, y el
      // aviso de abajo advierte de que faltan.
      listSentGreetings({ ...filtro, fromDate, toDate }).catch(() => null),
    ])
      .then(([cola, inmediatos]) => {
        if (!alive) return;
        const todo = [...(Array.isArray(cola) ? cola : []), ...(Array.isArray(inmediatos) ? inmediatos : [])];
        // Reordenar el conjunto: cada mitad viene ordenada por su cuenta, y
        // pegarlas dejaría los envíos del mismo día separados por media
        // pantalla — justo los que hay que comparar entre sí.
        todo.sort((a, b) => new Date(a.runAt) - new Date(b.runAt));
        setRows(todo);
        setPartial(inmediatos === null);
      })
      .catch((e) => { if (alive) setError(e?.message || 'error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fromDate, toDate, source, reloadKey]);

  const clientsById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const nameOf = (clientId) => {
    const c = clientsById.get(clientId);
    return c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : (es ? '(cliente eliminado)' : '(deleted client)');
  };
  const byWhom = (userId) => {
    const u = usersById.get(userId);
    return u?.name || u?.displayName || (userId === currentUser?.id ? (es ? 'tú' : 'you') : (es ? 'un compañero' : 'a teammate'));
  };

  // Un envío es "repetido" cuando otro AL MISMO DESTINATARIO, del mismo tipo de
  // saludo y el mismo día ya existe. Los cancelados no cuentan: no llegan a
  // nadie.
  //
  // La dirección forma parte de la llave a propósito, igual que en la regla que
  // aplica el servidor: en un hogar el titular y el cónyuge cuelgan del MISMO
  // cliente, así que sin ella dos saludos legítimos a dos personas distintas se
  // marcarían como repetidos. Una pantalla que grita "repetido" cuando no lo
  // hay se deja de mirar, y entonces tampoco avisa cuando sí lo es.
  const dupeKey = (r) => `${r.clientId}|${String(r.recipient || '').trim().toLowerCase()}|${r.source}|${localDayKey(r.runAt)}`;
  const dupeKeys = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      if (!ONE_PER_DAY.has(r.source) || !r.clientId || r.status === 'cancelled') continue;
      const k = dupeKey(r);
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rows]);

  const isDupe = (r) => ONE_PER_DAY.has(r.source) && r.clientId && r.status !== 'cancelled'
    && dupeKeys.has(dupeKey(r));

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => rows.filter((r) => {
    if (onlyDupes && !isDupe(r)) return false;
    if (!q) return true;
    return nameOf(r.clientId).toLowerCase().includes(q) || String(r.recipient || '').toLowerCase().includes(q);
  }), [rows, onlyDupes, q, dupeKeys, clientsById]);

  // Cancelar y editar solo lo pendiente, y solo si el servidor lo va a
  // permitir. Desde el punto 75 la regla ya no es "el autor o el dueño": es
  // CUALQUIERA DEL EQUIPO QUE PUEDA PROGRAMAR CAMPAÑAS — el mismo permiso que
  // hace falta para meterlas en la cola. Lissette, 2026-09-04: *"cuando
  // programe una campaña, dejar que todo el equipo de la misma agencia pueda
  // editarlo o desprogramarla"*. Antes, una campaña de 340 correos solo la
  // podía parar quien le dio al botón — y si esa persona estaba de vacaciones
  // el día que había que pararla, no se paraba.
  //
  // `canManage` lo calcula la aplicación con los mismos cuatro roles y el mismo
  // `hasSeatPermission('campaigns.edit')` que aplica la ruta. Si aquí y allí no
  // coinciden, el botón sale y el servidor contesta 403.
  const canCancel = (r) => r.status === 'pending' && canManage;

  // Cuántos envíos siguen en espera de esta misma campaña. Se cuenta sobre lo
  // que ya está en pantalla (la lista es de toda la agencia), sin otra llamada.
  const pendientesDeCampana = (campaignId) => (campaignId
    ? rows.filter((x) => x.campaignId === campaignId && x.status === 'pending').length
    : 0);

  const doCancel = async (r) => {
    const who = nameOf(r.clientId);
    // UNA CAMPAÑA PROGRAMADA SON MUCHAS FILAS, una por destinatario. Cancelar
    // la que se ve deja las otras 339 saliendo igual, así que aquí se
    // desprograma la campaña ENTERA — que es lo que ella pidió ("o
    // desprogramarla") y lo único que se puede hacer con las manos.
    const hermanas = pendientesDeCampana(r.campaignId);
    const laCampanaEntera = !!r.campaignId && hermanas > 1;
    const ok = await confirmDialog({
      title: laCampanaEntera
        ? (es ? 'Desprogramar la campaña' : 'Unschedule the campaign')
        : (es ? 'Cancelar este envío' : 'Cancel this send'),
      message: laCampanaEntera
        ? (es
          ? `Esta campaña tiene ${hermanas} correos en espera y NO saldrá ninguno. La campaña vuelve a quedar como borrador en Email Masivo, así que puedes editarla y volver a programarla.`
          : `This campaign has ${hermanas} emails waiting and none of them will go out. The campaign goes back to being a draft in Mass Email, so you can edit it and schedule it again.`)
        : (es
          ? `El correo a ${who} (${r.recipient}) no se enviará. Esto no se puede deshacer, pero puedes volver a programarlo.`
          : `The email to ${who} (${r.recipient}) will not be sent. This can't be undone, but you can schedule it again.`),
      confirmLabel: laCampanaEntera
        ? (es ? `Desprogramar los ${hermanas}` : `Unschedule all ${hermanas}`)
        : (es ? 'Cancelar el envío' : 'Cancel the send'),
      danger: true,
    });
    if (!ok) return;
    setBusyId(r.id);
    try {
      const n = await cancelScheduledSend(r.id, laCampanaEntera ? { scope: 'campaign' } : {});
      setRows((prev) => prev.map((x) => (
        (laCampanaEntera ? (x.campaignId === r.campaignId && x.status === 'pending') : x.id === r.id)
          ? { ...x, status: 'cancelled' }
          : x
      )));
      toast.success(laCampanaEntera
        ? (es ? `Campaña desprogramada — ${n} correos no saldrán.` : `Campaign unscheduled — ${n} emails will not go out.`)
        : (es ? 'Envío cancelado — no saldrá.' : 'Send cancelled — it will not go out.'));
    } catch (e) {
      toast.error((es ? 'No se pudo cancelar: ' : "Couldn't cancel: ") + (e?.message || ''));
    } finally {
      setBusyId('');
    }
  };

  // Solo lo que se ve AHORA y de verdad se puede cancelar. Marcar mas de lo
  // que se esta mirando es como se cancela sin querer el correo de otro: si
  // hay un filtro puesto, "Seleccionar todo" respeta ese filtro.
  const selectableIds = useMemo(() => visible.filter(canCancel).map((r) => r.id), [visible, currentUser]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selIds.has(id));
  const toggleSel = (id) => setSelIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelIds(allSelected ? new Set() : new Set(selectableIds));
  const exitSel = () => { setSelMode(false); setSelIds(new Set()); };

  const cancelSelected = async () => {
    const ids = selectableIds.filter((id) => selIds.has(id));
    if (!ids.length) return;
    const ok = await confirmDialog({
      title: es ? `¿Cancelar ${ids.length} envío(s)?` : `Cancel ${ids.length} send(s)?`,
      message: es
        ? `Esos correos no saldrán. No se puede deshacer, pero se pueden volver a programar. Los que ya se enviaron no se tocan.`
        : `Those emails will not go out. This can't be undone, but they can be scheduled again. Anything already sent is untouched.`,
      confirmLabel: es ? 'Cancelar los envíos' : 'Cancel the sends',
      danger: true,
    });
    if (!ok) return;
    setBulkBusy(true);
    const hechos = [];
    let fallidos = 0;
    // De cinco en cinco: no hay ruta de lote, y mandar cientos a la vez se
    // come el limite de peticiones y falla la mitad sin decir cual.
    for (let i = 0; i < ids.length; i += 5) {
      const tanda = ids.slice(i, i + 5);
      const res = await Promise.all(tanda.map((id) =>
        cancelScheduledSend(id).then(() => id).catch(() => null)));
      res.forEach((id) => { if (id) hechos.push(id); else fallidos++; });
    }
    // Se marca lo que SI se cancelo, aunque alguno fallara: dejar en pantalla
    // como "programado" algo ya cancelado es peor que decir cuantos no se pudo.
    if (hechos.length) {
      const set = new Set(hechos);
      setRows((prev) => prev.map((x) => (set.has(x.id) ? { ...x, status: 'cancelled' } : x)));
    }
    setBulkBusy(false);
    exitSel();
    if (fallidos) toast.error(es ? `No se pudieron cancelar ${fallidos}` : `Could not cancel ${fallidos}`);
    else toast.success(es ? `${hechos.length} cancelado(s) — no saldrán.` : `${hechos.length} cancelled — they will not go out.`);
  };

  const dupeRowCount = rows.filter(isDupe).length;
  const pendingCount = rows.filter((r) => r.status === 'pending').length;

  const chip = (on) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.76rem', fontWeight: 700,
    padding: '6px 11px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${on ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
    background: on ? 'rgba(0,212,255,.12)' : 'var(--c-surface,#0d1c2e)',
    color: on ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text,#c8dff0)',
  });

  return (
    <div>
      <div className="dkt-sec-title" style={{ margin: '0 0 .35rem' }}>
        {es ? 'Correos programados' : 'Scheduled emails'}
        <span style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 600 }}>
          {' '}· {pendingCount} {es ? 'en espera' : 'waiting'}
        </span>
      </div>
      <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', marginBottom: '1rem', lineHeight: 1.5 }}>
        {es
          ? 'Todo lo que la agencia dejó programado, más los saludos enviados en el momento (marcados con ⚡), para que el control de repetidos vea las dos mitades. Los saludos manuales anteriores al 17 de agosto de 2026 no quedaron marcados y no se pueden contar aquí.'
          : 'Everything the agency has queued, plus greetings sent immediately (marked ⚡), so the duplicate check sees both halves. Manual greetings sent before 17 Aug 2026 were never tagged and cannot be counted here.'}
      </div>

      {partial && (
        <div style={{
          padding: '.6rem .9rem', borderRadius: 10, marginBottom: '1rem',
          background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.3)',
          fontSize: '.76rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.45,
        }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6, color: '#ffb700' }} />
          {es
            ? 'No se pudieron leer los saludos enviados en el momento. Lo de abajo es solo la cola, así que puede haber repetidos que no se ven. Vuelve a intentarlo con Actualizar.'
            : "Couldn't read the greetings sent immediately. What follows is the queue only, so there may be duplicates you can't see here. Try Refresh."}
        </div>
      )}

      {/* Aviso de repetidos: lo que Wendy pedía, arriba del todo. */}
      {dupeRowCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '.7rem', flexWrap: 'wrap',
          padding: '.75rem 1rem', borderRadius: 11, marginBottom: '1rem',
          background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.3)',
        }}>
          <AlertTriangle size={17} style={{ color: 'var(--c-red,#ff3d6b)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 220, fontSize: '.8rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.45 }}>
            <b style={{ color: 'var(--c-red,#ff3d6b)' }}>
              {dupeRowCount} {es ? 'envíos repetidos' : 'duplicate sends'}
            </b>{' '}
            {es
              ? 'en este rango: el mismo cliente tiene más de un saludo del mismo tipo para el mismo día. Cancela los que sobren.'
              : 'in this range: the same client has more than one greeting of the same kind for the same day. Cancel the extras.'}
          </div>
          <button type="button" onClick={() => setOnlyDupes((v) => !v)} style={chip(onlyDupes)}>
            {onlyDupes ? (es ? 'Ver todos' : 'Show all') : (es ? 'Ver solo repetidos' : 'Show only duplicates')}
          </button>
        </div>
      )}

      {/* Selección múltiple */}
      {selectableIds.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          {!selMode ? (
            <button type="button" className="dkt-btn ghost xs" onClick={() => setSelMode(true)}
              title={es ? 'Marcar varios para cancelarlos de una vez' : 'Tick several to cancel them at once'}>
              ☑️ {es ? 'Seleccionar' : 'Select'}
            </button>
          ) : (
            <>
              <button type="button" className="dkt-btn ghost xs" onClick={toggleAll} disabled={bulkBusy}>
                {allSelected
                  ? (es ? 'Quitar selección' : 'Clear selection')
                  : (es ? `Seleccionar todo (${selectableIds.length})` : `Select all (${selectableIds.length})`)}
              </button>
              <button type="button" className="dkt-btn-d xs" onClick={cancelSelected} disabled={bulkBusy || selIds.size === 0}>
                <Ban size={12} /> {bulkBusy
                  ? (es ? 'Cancelando…' : 'Cancelling…')
                  : (es ? `Cancelar (${selIds.size})` : `Cancel (${selIds.size})`)}
              </button>
              <button type="button" className="dkt-btn-s xs" onClick={exitSel} disabled={bulkBusy}>
                {es ? 'Salir' : 'Done'}
              </button>
              <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
                {es ? 'Solo se marcan los que están en espera y puedes cancelar.'
                    : 'Only the ones still waiting that you may cancel can be ticked.'}
              </span>
            </>
          )}
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <label style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {es ? 'Desde' : 'From'}
          <DateField value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} lang={lang}
            style={{ fontSize: '.78rem', padding: '5px 8px', borderRadius: 8 }} />
        </label>
        <label style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {es ? 'Hasta' : 'To'}
          <DateField value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} lang={lang}
            style={{ fontSize: '.78rem', padding: '5px 8px', borderRadius: 8 }} />
        </label>
        <select className="dkt-inp" value={source} onChange={(e) => setSource(e.target.value)}
          style={{ fontSize: '.78rem', padding: '6px 8px', borderRadius: 8 }}>
          {SOURCES(es).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text3,#45688a)' }} />
          <input className="dkt-inp" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={es ? 'Buscar cliente o correo…' : 'Search client or email…'}
            style={{ width: '100%', fontSize: '.78rem', padding: '6px 10px 6px 27px', borderRadius: 8 }} />
        </div>
        <button type="button" onClick={() => setReloadKey((k) => k + 1)} title={es ? 'Actualizar' : 'Refresh'}
          style={{ ...chip(false), padding: '6px 10px' }}>
          <RefreshCw size={13} /> {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {/* Lista */}
      <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, overflow: 'hidden' }}>
        {loading && (
          <div style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
            <Clock size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
            {es ? 'Cargando la cola…' : 'Loading the queue…'}
          </div>
        )}
        {!loading && error && (
          <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--c-red,#ff3d6b)', fontSize: '.85rem' }}>
            {es ? 'No se pudo leer la cola: ' : "Couldn't read the queue: "}{error}
          </div>
        )}
        {!loading && !error && visible.length === 0 && (
          <div style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
            <Mail size={18} style={{ display: 'block', margin: '0 auto .5rem', opacity: .5 }} />
            {onlyDupes
              ? (es ? 'Ningún repetido en este rango. 🎉' : 'No duplicates in this range. 🎉')
              : (es ? 'No hay correos programados en este rango.' : 'No scheduled emails in this range.')}
          </div>
        )}
        {!loading && !error && visible.map((r, i) => {
          const st = STATUS[r.status] || { label: r.status, color: '#7aa3c0' };
          const dupe = isDupe(r);
          const when = new Date(r.runAt);
          return (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.7rem 1rem',
              borderTop: i > 0 ? '1px solid var(--c-border,#1a3550)' : 'none',
              background: dupe ? 'rgba(255,61,107,.06)' : 'transparent',
              borderLeft: dupe ? '3px solid var(--c-red,#ff3d6b)' : '3px solid transparent',
              opacity: r.status === 'cancelled' ? .55 : 1,
            }}>
              {selMode && (
                <div style={{ width: 20, flexShrink: 0 }}>
                  {canCancel(r) ? (
                    <input type="checkbox" checked={selIds.has(r.id)} onChange={() => toggleSel(r.id)}
                      disabled={bulkBusy} style={{ accentColor: 'var(--c-cyan,#00d4ff)', cursor: 'pointer' }} />
                  ) : (
                    <span title={es ? 'Este ya no se puede cancelar' : 'This one can no longer be cancelled'}
                      style={{ color: 'var(--c-text3,#45688a)' }}>—</span>
                  )}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '.5rem', flexWrap: 'wrap' }}>
                  <button type="button"
                    onClick={() => { const c = clientsById.get(r.clientId); if (c && onOpenClient) onOpenClient(c); }}
                    disabled={!clientsById.get(r.clientId)}
                    style={{
                      background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
                      fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)',
                      cursor: clientsById.get(r.clientId) ? 'pointer' : 'default', textAlign: 'left',
                    }}>
                    {nameOf(r.clientId)}
                  </button>
                  {dupe && (
                    <span style={{
                      fontSize: '.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em',
                      padding: '2px 7px', borderRadius: 5,
                      background: 'rgba(255,61,107,.15)', color: 'var(--c-red,#ff3d6b)',
                    }}>
                      {es ? 'Repetido' : 'Duplicate'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>
                  {r.recipient} · {(SOURCES(es).find((s) => s.id === r.source) || {}).label || r.source}
                  {r.repeatAnnually ? (es ? ' · cada año' : ' · yearly') : ''}
                  {' · '}{es ? 'por' : 'by'} {byWhom(r.userId)}
                  {r.immediate && (
                    <span title={es ? 'Se envió en el momento, no pasó por la cola' : 'Sent immediately — never queued'}
                      style={{ marginLeft: 6, color: '#ffb700', fontWeight: 700 }}>
                      <Zap size={10} style={{ verticalAlign: -1 }} /> {es ? 'al momento' : 'immediate'}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '.76rem', color: 'var(--c-text,#c8dff0)', fontVariantNumeric: 'tabular-nums' }}>
                  {when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
                <div style={{ fontSize: '.68rem', fontWeight: 700, color: st.color, marginTop: 1 }}>{st.label}</div>
              </div>
              {/* Ver lo que dice. Antes no habia forma: la lista no trae el
                  cuerpo y no habia por donde pedirlo. */}
              <button type="button" onClick={() => abrir(r)}
                title={es ? 'Ver lo que dice este correo' : 'See what this email says'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                  padding: '5px 10px', borderRadius: 8, fontFamily: 'inherit',
                  fontSize: '.7rem', fontWeight: 700, cursor: 'pointer',
                  border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
                  color: 'var(--c-text2,#7aa3c0)',
                }}>
                <Eye size={12} />{es ? 'Ver' : 'View'}
              </button>
              {canCancel(r) && (
                <button type="button" onClick={() => abrirEditor(r)}
                  title={es ? 'Cambiar la fecha, la hora o el texto' : 'Change the date, time or text'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                    padding: '5px 10px', borderRadius: 8, fontFamily: 'inherit',
                    fontSize: '.7rem', fontWeight: 700, cursor: 'pointer',
                    border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
                    color: 'var(--c-text2,#7aa3c0)',
                  }}>
                  <Pencil size={12} />{es ? 'Editar' : 'Edit'}
                </button>
              )}
              {canCancel(r) ? (
                <button type="button" onClick={() => doCancel(r)} disabled={busyId === r.id}
                  title={es ? 'Cancelar este envío' : 'Cancel this send'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                    padding: '5px 10px', borderRadius: 8, fontFamily: 'inherit',
                    fontSize: '.7rem', fontWeight: 700, cursor: busyId === r.id ? 'wait' : 'pointer',
                    border: '1px solid rgba(255,61,107,.35)', background: 'rgba(255,61,107,.1)',
                    color: 'var(--c-red,#ff3d6b)',
                  }}>
                  <Ban size={12} />{busyId === r.id ? (es ? '…' : '…') : (es ? 'Cancelar' : 'Cancel')}
                </button>
              ) : (
                // Desde el punto 75 ya no es "solo su autor": lo para
                // cualquiera del equipo que pueda programar campañas. Quien no
                // pueda es quien no tiene ese permiso — decirlo aquí evita el
                // 403 y explica qué hace falta.
                <span style={{ width: 78, flexShrink: 0, textAlign: 'right', fontSize: '.62rem', color: 'var(--c-text3,#45688a)' }}>
                  {r.status === 'pending' ? (es ? 'sin permiso para campañas' : 'no campaign permission') : ''}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {editor && (
        <Modal wide onClose={() => (editor.guardando ? null : setEditor(null))}
          title={`\u270f\ufe0f ${es ? 'Cambiar este envio' : 'Change this send'}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>
              {es ? 'Para' : 'To'} {editor.fila.recipient}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {es ? 'Fecha' : 'Date'}
                <DateField value={editor.fecha} lang={lang}
                  onChange={(ev) => setEditor((x) => ({ ...x, fecha: ev.target.value }))}
                  style={{ fontSize: '.82rem', padding: '6px 9px', borderRadius: 8 }} />
              </label>
              <label style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {es ? 'Hora' : 'Time'}
                <input className="dkt-inp" type="time" value={editor.hora}
                  onChange={(ev) => setEditor((x) => ({ ...x, hora: ev.target.value }))}
                  style={{ fontSize: '.82rem', padding: '6px 9px', borderRadius: 8 }} />
              </label>
            </div>

            {/* UNA CAMPANA SE MUEVE ENTERA. Son una fila por destinatario: mover
                una sola dejaria las demas saliendo a la hora vieja. */}
            {!!editor.fila.campaignId && (
              <div style={{
                padding: '.7rem .85rem', borderRadius: 9, fontSize: '.76rem', lineHeight: 1.5,
                background: 'var(--c-amber-dim)', color: 'var(--c-amber,#ffb700)',
                border: '1px solid rgba(255,183,0,.25)',
              }}>
                {es
                  ? `Esto es parte de una campana: la nueva fecha se aplica a los ${pendientesDeCampana(editor.fila.campaignId)} correos que siguen en espera. El TEXTO no se puede cambiar aqui, porque cada copia lleva ya el nombre de su destinatario — desprograma la campana, editala en Email Masivo y vuelve a programarla.`
                  : `This is part of a campaign: the new date applies to all ${pendientesDeCampana(editor.fila.campaignId)} emails still waiting. The TEXT cannot be changed here, because each copy already carries its recipient's name — unschedule the campaign, edit it in Mass Email and schedule it again.`}
              </div>
            )}

            {!editor.fila.campaignId && editor.cargando && (
              <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Cargando el texto…' : 'Loading the text…'}</div>
            )}
            {!editor.fila.campaignId && !editor.cargando && editor.error && (
              <div style={{
                padding: '.7rem .85rem', borderRadius: 9, fontSize: '.76rem', lineHeight: 1.5,
                background: 'var(--c-amber-dim)', color: 'var(--c-amber,#ffb700)',
                border: '1px solid rgba(255,183,0,.25)',
              }}>
                {editor.error} {es ? 'La fecha y la hora si se pueden cambiar.' : 'The date and time can still be changed.'}
              </div>
            )}
            {!editor.fila.campaignId && !editor.cargando && !editor.error && (
              <>
                <label style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {es ? 'Asunto' : 'Subject'}
                  <input className="dkt-inp" value={editor.subject}
                    onChange={(ev) => setEditor((x) => ({ ...x, subject: ev.target.value }))}
                    style={{ fontSize: '.82rem', padding: '6px 9px', borderRadius: 8 }} />
                </label>
                <label style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {es ? 'Texto del correo (HTML)' : 'Email body (HTML)'}
                  <textarea className="dkt-inp" value={editor.body} rows={10}
                    onChange={(ev) => setEditor((x) => ({ ...x, body: ev.target.value }))}
                    style={{ fontSize: '.78rem', padding: '8px 9px', borderRadius: 8, fontFamily: 'var(--font-mono)', resize: 'vertical' }} />
                </label>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="dkt-btn-s" onClick={() => setEditor(null)} disabled={editor.guardando}>
                {es ? 'Salir sin guardar' : 'Close without saving'}
              </button>
              <button type="button" className="dkt-btn" onClick={guardarEditor} disabled={editor.guardando}>
                {editor.guardando ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {visor && (
        <Modal xl onClose={() => setVisor(null)}
          title={`✉️ ${es ? 'Lo que dice este correo' : 'What this email says'}`}>
          {visor.cargando && (
            <div style={{ padding: '1.4rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.82rem' }}>
              {es ? 'Cargando…' : 'Loading…'}
            </div>
          )}
          {!visor.cargando && visor.error && (
            <div style={{
              padding: '.9rem 1rem', borderRadius: 9, fontSize: '.82rem', lineHeight: 1.5,
              background: 'var(--c-amber-dim)', color: 'var(--c-amber,#ffb700)',
              border: '1px solid rgba(255,183,0,.25)',
            }}>
              {visor.error}
            </div>
          )}
          {!visor.cargando && visor.fila && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
              <div>
                <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontFamily: 'var(--font-mono)' }}>
                  {es ? 'Asunto' : 'Subject'}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
                  {visor.fila.subject || (es ? '(sin asunto)' : '(no subject)')}
                </div>
                <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
                  {es ? 'Para' : 'To'} {visor.fila.recipient} · {new Date(visor.fila.runAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
              <div style={{ maxHeight: 340, overflowY: 'auto', background: '#fff', color: '#0a1628', borderRadius: 9, border: '1px solid var(--c-border,#1a3550)', padding: '.85rem' }}>
                <div dangerouslySetInnerHTML={safeHtml(visor.fila.body || '')} />
              </div>
              <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>
                {es
                  ? 'Este es el correo de ESTE destinatario, ya con su nombre puesto. Cada uno recibe el suyo.'
                  : 'This is THIS recipient\'s copy, with their name already filled in. Each one gets their own.'}
              </div>
              {/* Reenviar: no manda nada desde aqui. Lleva el asunto y el
                  cuerpo a Email Masivo, donde se eligen los destinatarios. Un
                  boton que dispara cientos de correos desde una pantalla de
                  historial es un accidente esperando a pasar. */}
              <div>
                <button type="button"
                  onClick={() => {
                    // Se DEJA, no se grita: Email Masivo se carga bajo demanda,
                    // asi que el CustomEvent que habia aqui salia antes de que
                    // existiera quien lo escuchara y el asunto y el cuerpo se
                    // perdian — se aterrizaba en Componer en blanco.
                    dejarPendiente(PENDIENTE_EMAIL_MASIVO, {
                      subject: visor.fila.subject || '', body: visor.fila.body || '',
                    });
                    window.dispatchEvent(new CustomEvent('deskte:navigate', { detail: { view: 'mass_email' } }));
                    setVisor(null);
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'var(--c-cyan,#00d4ff)', color: '#06121f', border: 'none',
                    borderRadius: 9, padding: '.5rem .9rem', fontSize: '.8rem', fontWeight: 800,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                  <Send size={13} /> {es ? 'Reenviar' : 'Resend'}
                </button>
                <span style={{ marginLeft: 10, fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>
                  {es ? 'Lo lleva a Email Masivo para que elijas a quién.' : 'Takes it to Mass Email so you pick who.'}
                </span>
              </div>
            </div>
          )}
        </Modal>
      )}

      {!loading && !error && rows.length > 0 && (
        <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: '.7rem', textAlign: 'right' }}>
          {visible.length === rows.length
            ? `${rows.length} ${es ? 'envíos' : 'sends'}`
            : `${visible.length} ${es ? 'de' : 'of'} ${rows.length} ${es ? 'envíos' : 'sends'}`}
        </div>
      )}
    </div>
  );
}

export default ScheduledSendsView;
